import { Cubit } from "blac-next";
import type {
  DropdownActionSheetProps,
  DropdownActionSheetState
} from "./DropdownActionSheet.types";
import {
  calculateHighlightPosition,
  calculatePlacement,
  flattenButtons
} from "./DropdownActionSheet.utils";
import { DEFAULT_TRIGGER_POSITION } from "./DropdownActionSheet.constants";

const HIGHLIGHT_TRANSITION_POSITION =
  "top 180ms cubic-bezier(0.05, 0.9, 0.2, 1), height 180ms cubic-bezier(0.05, 0.9, 0.2, 1), opacity 150ms ease-out";

const HIGHLIGHT_TRANSITION_FADE = "opacity 150ms ease-out";

const createInitialState = (
  props: DropdownActionSheetProps
): DropdownActionSheetState => ({
  open: props.isOpen,
  placement: "bottom start",
  title: props.options.title,
  message: props.options.message,
  buttons: props.options.buttons,
  iconPosition: props.options.iconPosition ?? "left",
  toggleAppearance: props.options.toggleAppearance ?? "switch"
});

export class DropdownActionSheetBloc extends Cubit<
  DropdownActionSheetState,
  DropdownActionSheetProps
> {
  static isolated = true;

  private isFirstHover = true;
  private highlightElement: HTMLDivElement | null = null;
  private menuElement: HTMLDivElement | null = null;

  props: DropdownActionSheetProps;

  constructor(
    props: DropdownActionSheetProps = {
      isOpen: false,
      onDidDismiss: () => {},
      options: { buttons: [] }
    }
  ) {
    super(createInitialState(props));
    this.props = props;

    if (this.state.open) {
      const position = props.triggerPosition ?? DEFAULT_TRIGGER_POSITION;
      const { placement } = calculatePlacement(position, props.options);
      this.patch({ placement });
    }
  }

  get backdropDismiss(): boolean {
    return this.props.options.backdropDismiss ?? true;
  }

  get cancelButtonId(): string {
    return this.props.options.cancelButtonId ?? "cancel";
  }

  updateProps = (newProps: DropdownActionSheetProps): void => {
    this.props = newProps;
    this.patch(createInitialState(newProps));
  };

  setElements = (
    highlightElement: HTMLDivElement | null,
    menuElement: HTMLDivElement | null
  ): void => {
    this.highlightElement = highlightElement;
    this.menuElement = menuElement;
  };

  updatePosition = (position: { x: number; y: number }): void => {
    const { placement } = calculatePlacement(position, this.props.options);
    this.patch({ placement });
  };

  updateHighlight = (itemElement: HTMLElement | null): void => {
    if (!this.highlightElement) {
      return;
    }

    const position = calculateHighlightPosition(itemElement, this.menuElement);

    if (!position) {
      this.highlightElement.style.opacity = "0";
      return;
    }

    // First hover: fade in without position animation
    // Subsequent hovers: animate position
    const transition = this.isFirstHover
      ? HIGHLIGHT_TRANSITION_FADE
      : HIGHLIGHT_TRANSITION_POSITION;

    if (this.isFirstHover) {
      this.isFirstHover = false;
    }

    this.highlightElement.style.transition = transition;
    this.highlightElement.style.top = `${position.top}px`;
    this.highlightElement.style.height = `${position.height}px`;
    this.highlightElement.style.opacity = "1";
  };

  hideHighlight = (): void => {
    this.isFirstHover = true;
    if (this.highlightElement) {
      this.highlightElement.style.opacity = "0";
      this.highlightElement.dataset.pressed = "false";
    }
  };

  setHighlightPressed = (pressed: boolean): void => {
    if (this.highlightElement) {
      this.highlightElement.dataset.pressed = pressed ? "true" : "false";
    }
  };

  dismiss = (): void => {
    // Backdrop onClick and react-aria's interact-outside can both request a
    // close for the same interaction; only the first should take effect.
    if (!this.state.open) {
      return;
    }
    if (!this.backdropDismiss) {
      return;
    }
    this.patch({ open: false });
    this.props.onDidDismiss(this.cancelButtonId);
  };

  selectOption = (buttonId: string): void => {
    if (!this.state.open) {
      return;
    }
    this.patch({ open: false });
    this.props.onDidDismiss(buttonId);
  };

  /**
   * Switching an inline-children parent back ON re-enables its descendants,
   * which were collapsed out of view while it was off. That has to happen here
   * rather than via the caller's props: the sheet renders into a detached root
   * (ActionSheetController) and never sees fresh props while open. Each
   * re-enabled descendant is reported through its own `onToggle`, or the
   * consumer keeps it off and it returns off on reopen.
   */
  toggleButton = (buttonId: string): void => {
    type Btn = (typeof this.state.buttons)[number];
    const button = flattenButtons(this.state.buttons).find(
      (b) => b.id === buttonId
    );
    if (!button) {
      return;
    }
    const newValue = !button.toggled;

    const reEnabledIds: string[] = [];

    const enableInlineDescendants = (children: readonly Btn[]): Btn[] =>
      children.map((c) => {
        const isToggle = c.type === "toggle";
        if (isToggle && c.toggled !== true) {
          reEnabledIds.push(c.id);
        }
        return {
          ...c,
          ...(isToggle ? { toggled: true } : {}),
          children:
            c.childrenLayout === "inline" && c.children?.length
              ? enableInlineDescendants(c.children)
              : c.children
        };
      });

    const revealsCollapsedChildren =
      newValue && button.childrenLayout === "inline";

    const updateButtons = (buttons: readonly Btn[]): Btn[] =>
      buttons.map((b) => {
        if (b.id === buttonId) {
          return revealsCollapsedChildren && b.children?.length
            ? {
                ...b,
                toggled: newValue,
                children: enableInlineDescendants(b.children)
              }
            : { ...b, toggled: newValue };
        }
        if (b.children?.length) {
          return { ...b, children: updateButtons(b.children) };
        }
        return b;
      });

    this.patch({ buttons: updateButtons(this.state.buttons) });
    this.props.options.onToggle?.(buttonId, newValue);
    for (const id of reEnabledIds) {
      this.props.options.onToggle?.(id, true);
    }
  };
}
